// 📁 src/components/BlogPost.jsx

import { Link, useParams } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import { loadArticles } from "../data/loadArticles";
import { PaperBG, PaperDarkBG } from "../kits/DesignKit";
import Preloader from "../components/Preloader";
import { MonoButton } from "../kits/DesignKit/PatternKit";
import { FaCode } from "react-icons/fa";
import { loadModelViewer } from "../utils/loadModelViewer";
import CollapsibleCodeBlock_screen from "../components/CollapsibleCodeBlock_screen";
import RoughBorder from "../kits/LayoutKit/RoughBorder";
import BlogListBlog from "./BlogListBlog";

// Eigenes Bild-Block-Component mit useRef
function EditorJsImageBlock({ src, alt, i }) {
  const imageRef = useRef();
  return (
    <div key={i} className="my-6 p-4 
    bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7] rounded shadow relative" ref={imageRef}>
      <img
        src={src}
        alt={alt}
        className="w-full max-w-xl mx-auto rounded-md border border-gray-300"
      />
      {alt && (
        <figcaption className="mt-2 text-xs text-center text-gray-600 font-mono">
          {alt}
        </figcaption>
      )}
      <RoughBorder
        parentRef={imageRef}
        radius={10}
        padding={2}
       color="#AC8E66" 
        strokeWidth={0.3}
        roughness={1}
        bowing={2}
      />
    </div>
  );
}

// Eigenes YouTube-Block-Component mit useRef
function YoutubeBlock({ url, i }) {
  const youtubeRef = useRef();
  let videoId = null;
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes("youtube.com") || parsed.hostname.includes("youtu.be")) {
      videoId = parsed.searchParams.get("v") || parsed.pathname.split('/').pop();
    }
  } catch {}

  return (
    <div key={i} className="my-4 rounded-lg overflow-hidden relative" ref={youtubeRef}>
      <div className="aspect-video border border-gray-800 
bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
      pt-2 pb-2 pl-2 pr-2 rounded-lg overflow-hidden">
        {videoId ? (
          <iframe
            className="w-full h-full border border-gray-800 rounded-lg"
            src={`https://www.youtube.com/embed/${videoId}`}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title={`youtube-${i}`}
          />
        ) : (
          <p className="p-4 text-red-500 font-mono">⚠️ Kein Video gefunden. Das ist ja blöd</p>
        )}
      </div>
      <RoughBorder
        parentRef={youtubeRef}
        radius={10}
        padding={2}
       color="#AC8E66" 
        strokeWidth={1.1}
        roughness={1}
        bowing={2}
      />
    </div>
  );
}

function renderEditorJsBlocks(blocks = []) {
  return blocks.map((block, i) => {
    const data = block?.data || {};
    switch (block.type) {
      case "header": {
        const Tag = `h${data.level}`;
        return (
          <PaperDarkBG
            key={i}
            className="mt-8 mb-4 rounded-lg px-4 py-2 relative -top-1 inline-block w-full"
          >
            <Tag className="text-sm font-mono text-amber-100 text-shadow flex items-center gap-x-2">
              <FaCode className="text-brand" />
              {data.text}
            </Tag>
          </PaperDarkBG>
        );
      }
      case "paragraph":
        return (
          <p key={i} className="mb-4 ml-4 leading-relaxed font-mono text-xs">
            {data.text}
          </p>
        );
      case "list": {
        const items = data.items || [];
        if (!items.length) return null;
        const Tag = data.style === "unordered" ? "ul" : "ol";
        return (
          <Tag key={i} className={`ml-6 mb-4 list-${data.style === "unordered" ? "disc" : "decimal"} font-mono text-xs`}>
            {items.map((item, idx) => {
              const text = typeof item === "string" ? item : item.content || item.text || "";
              return <li key={idx}>{text}</li>;
            })}
          </Tag>
        );
      }
      case "quote":
        return (
          <PaperDarkBG key={i} className="my-4 rounded-lg">
            <blockquote className="border-l-4 border-brand pl-4 pr-2 py-4 italic text-xs font-mono text-white">
              <p>{data.text}</p>
              {data.caption && (
                <cite className="block text-right font-mono mt-2">
                  — {data.caption}
                </cite>
              )}
            </blockquote>
          </PaperDarkBG>
        );
      case "image": {
        const src = data.file?.url || data.src;
        const alt = data.caption || data.alt || "Bild";
        if (!src) {
          return (
            <div key={i} className="text-red-600 font-mono text-xs my-4">
              ⚠️ Kein Bild gefunden im Block {i}
              <pre>{JSON.stringify(data, null, 2)}</pre>
            </div>
          );
        }
        return <EditorJsImageBlock src={src} alt={alt} i={i} key={i} />;
      }
      case "youtube":
        return <YoutubeBlock url={data.url} i={i} key={i} />;
      case "code":
        return (
          <CollapsibleCodeBlock_screen code={block.data.code} language={block.data.language} key={i} />
        );
      case "cta":
        return (
          <PaperDarkBG key={i} className="my-6 text-center rounded-lg">
            <a
              href={data.url || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block"
            >
              <MonoButton className="w-full  text-xs px-2 py-1 pt-2 pb-2 hover: bg-gray-950 text-brand text-shadow">
                {data.text || "Weiter"}
              </MonoButton>
            </a>
          </PaperDarkBG>
        );
      case "delimiter":
        return <hr key={i} className="my-8 border-brand" />;
      case "embed":
        return (
          <PaperDarkBG key={i} className="my-6 rounded-lg overflow-hidden">
            <div className="aspect-w-16 aspect-h-9">
              <iframe
                src={data.embed}
                title={data.caption || `Embed-${i}`}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
          </PaperDarkBG>
        );
      case "checklist":
        return (
          <PaperBG key={i} className="mb-4 p-4 rounded-lg">
            <ul className="list-none ml-0">
              {data.items.map((item, idx) => (
                <li key={idx} className="flex items-center mb-2">
                  <input type="checkbox" checked={item.checked} readOnly className="mr-2" />
                  <span className="font-mono text-xs text-gray-800">{item.text}</span>
                </li>
              ))}
            </ul>
          </PaperBG>
        );
      case "table":
        return (
          <PaperBG key={i} className="mb-6 p-4 rounded-lg overflow-x-auto">
            <table className="table-auto border-collapse w-full">
              <tbody>
                {data.content.map((row, r) => (
                  <tr key={r}>
                    {row.map((cell, c) => (
                      <td key={c} className="border px-4 py-2 text-left font-mono text-xs">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </PaperBG>
        );
      case "model3d": {
        const src = data.src;
        const alt = data.alt || "3D Modell";
        if (!src) {
          return (
            <div key={i} className="text-red-600 font-mono text-xs my-4">
              ⚠️ Kein 3D-Modell angegeben im Block {i}
              <pre>{JSON.stringify(data, null, 2)}</pre>
            </div>
          );
        }
        return (
          <div key={i} className="my-6 p-4 rounded-lg filter grayscale">
            <model-viewer
              src={src}
              alt={alt}
              auto-rotate
              camera-controls
              ar
              ios-src={src}
              exposure="1.3"
              shadow-intensity="1"
              type="model/gltf-binary"
              style={{ width: "100%", height: "400px" }}
            />
            <figcaption className="mt-2 text-xs text-center text-gray-600 font-mono">
              {alt || src.split("/").pop()}
            </figcaption>
          </div>
        );
      }
      default:
        return (
          <pre
            key={i}
            className="bg-yellow-100 text-red-600 text-xs p-2 rounded overflow-x-auto font-mono"
          >
            ⚠️ Unbekannter Block-Typ: {block.type}
            {"\n"}
            {JSON.stringify(data, null, 2)}
          </pre>
        );
    }
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Ref für das große Artikelbild oben
  const imageBoxRef = useRef();

  useEffect(() => {
    async function fetchPost() {
      setIsLoading(true);
      setError(null);

      const fetchedArticle = await loadArticles(slug);

      if (fetchedArticle) {
        setPost(fetchedArticle);
      } else {
        setError("Artikel konnte nicht gefunden werden oder ist ungültig.");
      }

      await new Promise(resolve => setTimeout(resolve, 500));
      setIsLoading(false);
    }

    fetchPost();
  }, [slug]);

  useEffect(() => {
    loadModelViewer();
  }, []);

  return (
    <Preloader isReady={!isLoading}>
      {error ? (
        <div className="text-red-500 bg-slate-900 text-center text-lg py-10 font-mono">
          {error}
        </div>
      ) : (
        post && (
          <PaperBG className="py-10">
            <article className="max-w-4xl w-full mx-auto pt-4 px-4 font-mono">
              {/* Bereich für GROSSE Bildschirme */}
              <PaperDarkBG className="rounded-lg hidden sm:block">
                <div className="max-w-4xl mx-auto px-5 py-2 rounded-lg">
                  <nav className="text-xs text-amber-100 mb-0 font-mono">
                    <Link to="/lernstudio" className="hover:text-brand text-amber-100">LernStudio</Link>
                    {" / "}
                    <Link to="/lernstudio?tab=blog" className="hover:text-brand text-amber-100">Wissen Blog</Link>
                    {" / "}
                    <span className="text-brand text-shadow">{post.title}</span>
                  </nav>
                </div>
              </PaperDarkBG>

              {/* Bereich für KLEINE Bildschirme */}
              <div className="rounded-lg sm:hidden bg-gray-50/60 backdrop-blur px-5 py-2">
                <nav className="text-xs text-gray-800 mb-0 font-mono">
                  <Link to="/lernstudio" className="hover:text-brand text-gray-800">LernStudio</Link>
                  {" / "}
                  <Link to="/lernstudio?tab=blog" className="hover:text-brand text-shadow py-1 px-1 pt-0 pb-0">Wissen Blog</Link>
                  {" / "} <br />
                  <div className="mt-1 ">
                    <span className=" text-black bg-brand">{post.title}</span>
                  </div>
                </nav>
              </div>

              <div className="py-5">
                <PaperDarkBG className="rounded-md">
                  <div className="grid grid-cols-[auto_1fr] gap-x-2 items-start text-amber-100 px-4 py-2">
                    <div className="grid grid-rows-[auto_auto] gap-y-0">
                      <h1 className="font-mono text-sm mt-2">{post.title}</h1>
                      {post.subtitle && (
                        <h2 className="font-mono text-xs text-amber-100 mt-1"></h2>
                      )}
                    </div>
                  </div>
                </PaperDarkBG>
                <div className="pt-10 px-3">{post.subtitle}</div>
              </div>

              {/* Blog-Hauptbild mit RoughBorder */}
              {post.image && (
                <div
                  ref={imageBoxRef}
                  className="block w-full md:inline-block md:max-w-prose 
                  bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]
                  px-4 py-4 shadow-lg relative -top-1"
                >
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full object-cover rounded-md 
                    bg-gradient-to-br from-[#FAF6F1] to-[#F5EFE7]"
                  />
                  <RoughBorder
                    parentRef={imageBoxRef}
                    radius={10}
                    padding={2}
                  color="#AC8E66" 
                    strokeWidth={1.1}
                    roughness={1}
                    bowing={2}
                  />
                </div>
              )}

              <div className="mt-4 text-xs">
                {Array.isArray(post.blocks) ? (
                  renderEditorJsBlocks(post.blocks)
                ) : (
                  <p className="whitespace-pre-line">{post.content}</p>
                )}
              </div>
            </article>
            {post.content?.blocks && Array.isArray(post.content.blocks) && (
              <div className="mt-8">
                {renderEditorJsBlocks(post.content.blocks)}
              </div>
            )}
            {post && <BlogListBlog currentSlug={slug} max={3} />}
          </PaperBG>
        )
      )}
    </Preloader>
  );
}
